import type {FileDiffReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import type {LineRange} from '@github-ui/diff-lines'
import {NestableActionMenu} from '@github-ui/nestable-action-menu'
import {AlertIcon, CopilotIcon} from '@primer/octicons-react'
import {ActionList, Spinner} from '@primer/react'
import {useMemo} from 'react'
import {DiffLinesAttachMenuItem} from '../shared/DiffLinesAttachMenuItem'
import {DiffLinesDiscussMenuItem} from '../shared/DiffLinesDiscussMenuItem'
import {DiffLinesExplainMenuItem} from '../shared/DiffLinesExplainMenuItem'
import {DiffChatWrapper, type DiffChatWrapperQueryVariables} from './DiffChatWrapper'

interface ContextMenuWrapperProps {
  queryVariables: DiffChatWrapperQueryVariables
  selectedRange?: LineRange
}

// wrapper/loader
export const CopilotDiffChatContextMenu: React.FC<ContextMenuWrapperProps> = ({queryVariables, selectedRange}) => (
  <DiffChatWrapper
    queryVariables={queryVariables}
    errorFallback={<MenuItemError />}
    suspenseFallback={<MenuItemLoading />}
    componentProps={{selectedRange}}
    Component={BaseCopilotDiffChatContextMenu}
  />
)

const MenuItemError: React.FC = () => (
  <ActionList.Item disabled>
    <ActionList.LeadingVisual>
      <AlertIcon />
    </ActionList.LeadingVisual>
    Copilot failed to load
  </ActionList.Item>
)

const MenuItemLoading: React.FC = () => (
  <ActionList.Item disabled>
    <ActionList.LeadingVisual>
      <Spinner size="small" />
    </ActionList.LeadingVisual>
    Copilot is loading...
  </ActionList.Item>
)

interface BaseCopilotDiffChatContextMenuProps {
  fileDiffReference: FileDiffReference
  selectedRange?: LineRange
}

// actual contents
const BaseCopilotDiffChatContextMenu: React.FC<BaseCopilotDiffChatContextMenuProps> = ({
  fileDiffReference,
  selectedRange,
}) => {
  const referenceWithRange = useMemo(() => {
    const ref = {...fileDiffReference}
    if (selectedRange) {
      ref.selectedRange = {
        start: `${selectedRange?.startOrientation[0]?.toUpperCase() ?? ''}${selectedRange?.startLineNumber}`,
        end: `${selectedRange?.endOrientation[0]?.toUpperCase() ?? ''}${selectedRange?.endLineNumber}`,
      }
    }
    return ref
  }, [fileDiffReference, selectedRange])

  return (
    <NestableActionMenu
      anchor={
        <NestableActionMenu.MenuItemAnchor leadingVisual={<CopilotIcon />}>Copilot</NestableActionMenu.MenuItemAnchor>
      }
    >
      <DiffLinesDiscussMenuItem fileDiffReference={referenceWithRange} eventContext={{prx: true}} />
      <DiffLinesExplainMenuItem fileDiffReference={referenceWithRange} eventContext={{prx: true}} />
      <DiffLinesAttachMenuItem fileDiffReference={referenceWithRange} eventContext={{prx: true}} />
    </NestableActionMenu>
  )
}

try{ CopilotDiffChatContextMenu.displayName ||= 'CopilotDiffChatContextMenu' } catch {}
try{ MenuItemError.displayName ||= 'MenuItemError' } catch {}
try{ MenuItemLoading.displayName ||= 'MenuItemLoading' } catch {}
try{ BaseCopilotDiffChatContextMenu.displayName ||= 'BaseCopilotDiffChatContextMenu' } catch {}